import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginEmployee } from "../redux/actions/authActions";
import { useNavigate } from "react-router-dom";
import logo from "../images/wb_logo.png";
import { Eye, EyeOff, LogIn, Lock, User } from "lucide-react";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { loading, user, error } = useSelector((state) => state.auth);

  useEffect(() => {
    if (user) {
      navigate("/home");
    }
  }, [user, navigate]);

  const handleLogin = (e) => {
    e.preventDefault();
    dispatch(loginEmployee({ phone, password }));
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#FFF5EB] via-[#FFFFFF] to-[#ECFFF3] flex items-center justify-center px-4 py-10">

      {/* Background Glow */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-orange-200/40 blur-3xl rounded-full"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-green-200/40 blur-3xl rounded-full"></div>

      <div className="w-full max-w-md relative z-10">

        {/* Logo & Header */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF7500] to-[#00A650] blur-2xl opacity-30 rounded-full"></div>

            <img
              src={logo}
              alt="Logo"
              className="relative h-28 mx-auto rounded-3xl bg-white p-3 shadow-2xl border border-orange-100"
            />
          </div>

          <h1 className="mt-5 text-3xl font-extrabold text-gray-800 tracking-tight">
            MAA Canteen Monitoring
          </h1>

          <div className="flex items-center justify-center mt-3 gap-2">
            <div className="w-10 h-1 rounded-full bg-[#FF7500]"></div>
            <div className="w-10 h-1 rounded-full bg-[#00A650]"></div>
          </div>

          <p className="mt-4 text-sm text-gray-600 font-medium">
            State Urban Development Agency
          </p>

          <p className="text-xs text-gray-500">
            Government of West Bengal
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white/85 backdrop-blur-xl rounded-[30px] shadow-2xl border border-white p-8">

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF7500] to-[#00A650] flex items-center justify-center shadow-lg">
              <LogIn className="text-white w-8 h-8" />
            </div>
          </div>

          {/* Welcome */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800">
              Welcome Back
            </h2>

            <p className="text-sm text-gray-500 mt-2">
              Sign in to continue
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-500 text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">

            {/* Phone */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Phone Number
              </label>

              <div className="relative">
                <User className="absolute left-4 top-3.5 w-5 h-5 text-[#FF7500]" />

                <input
                  type="text"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-orange-100 bg-[#FFFDFC] focus:outline-none focus:ring-2 focus:ring-[#FF7500]/30 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Password
              </label>

              <div className="relative">
                <Lock className="absolute left-4 top-3.5 w-5 h-5 text-[#00A650]" />

                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-14 py-3.5 rounded-2xl border border-green-100 bg-[#FCFFFD] focus:outline-none focus:ring-2 focus:ring-[#00A650]/30 focus:border-transparent transition-all"
                  required
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-3 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full relative overflow-hidden bg-gradient-to-r from-[#FF7500] to-[#00A650] text-white py-4 rounded-2xl font-semibold shadow-lg transition-all duration-300 ${
                loading
                  ? "opacity-70 cursor-not-allowed"
                  : "hover:scale-[1.02] active:scale-[0.99]"
              }`}
            >
              {loading ? (
                <div className="flex justify-center items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                  Signing In...
                </div>
              ) : (
                <div className="flex justify-center items-center gap-2">
                  <LogIn className="w-5 h-5" />
                  Sign In
                </div>
              )}
            </button>

            {/* Security Note */}
            <div className="pt-5 border-t border-gray-100">
              <div className="flex items-center justify-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-[#00A650]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                <p className="text-xs text-gray-500">
                  Secure Government Authentication
                </p>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} State Urban Development Agency
          </p>

          <p className="text-xs text-gray-400 mt-1">
            Department of Urban Development & Municipal Affairs
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;